import { createContext, useContext, useEffect } from 'react';
import {
  LoadingSponsor,
  Sponsor,
  useSponsorsQuery,
} from 'api/insurance/sponsors';
import { StandardSelectOption } from 'components/form/Select';
import { FCWithChildren } from 'helpers/types/FCWithChildren';
import { useCurrentUser } from './CurrentUserContext';
import {
  isOtherSponsor,
  sponsorToSelectOption,
} from 'hooks/useInsuranceOptions';
import { useMergeState } from 'hooks/useMergeState';

interface SponsorsState {
  sponsors: Sponsor[];
  selectOptions: StandardSelectOption[];
  isLoading?: boolean;
  isCurrentAccountSponsorOther?: boolean;
  accountSponsorCanEligibilityCheck?: boolean;
}

export interface ISponsorsContext extends SponsorsState {}

const SponsorsContext = createContext<ISponsorsContext>({
  sponsors: [],
  selectOptions: [],
  isLoading: false,
});

export const SponsorsProvider: FCWithChildren<{}> = ({ children }) => {
  const { data: sponsors = [LoadingSponsor], isLoading } = useSponsorsQuery();
  const [state, setState] = useMergeState<SponsorsState>({
    sponsors: [LoadingSponsor],
    selectOptions: [],
  });

  const { state: userState } = useCurrentUser();
  const account = userState.account;

  useEffect(() => {
    if (!isLoading && sponsors.length > 0) {
      const sortedSponsors = [...sponsors].sort(
        (a, b) => a.displayOrder - b.displayOrder,
      );
      const selectOptions = sortedSponsors.map(sponsorToSelectOption);
      const isCurrentAccountSponsorOther = isOtherSponsor(
        sortedSponsors,
        account?.insuranceCarrier ?? '',
      );

      setState({
        sponsors: sortedSponsors,
        selectOptions,
        isCurrentAccountSponsorOther,
        accountSponsorCanEligibilityCheck: account?.insuranceCarrier !== 'None',
        isLoading: false,
      });
    }
  }, [isLoading, sponsors, account, setState]);

  const ctx: ISponsorsContext = {
    ...state,
  };

  return (
    <SponsorsContext.Provider value={ctx}>{children}</SponsorsContext.Provider>
  );
};

export const useSponsors = () => {
  const context = useContext<ISponsorsContext>(SponsorsContext);

  if (!context)
    throw new Error(
      '!--- useSponsors must be used inside a ConsultProvider ---!',
    );

  return context;
};
